import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Projects.css';
import bulsufindImg from '../../assets/projects/bulsufind.png';
import codingChroniclesImg from '../../assets/projects/coding-chronicles.png';
import bulsuClassroomImg from '../../assets/projects/bulsu-classroom.png';
import barbershopAppImg from '../../assets/projects/barber-appointment.png';

const projects = [
  {
    id: 'barbershop-appointment',
    title: 'Barbershop Appointment System',
    image: barbershopAppImg,
    alt: 'Barbershop Appointment System Screenshot',
    brief: 'A full-stack web booking system with dynamic scheduling, service catalog, client management, and automated calendar slot reservations.',
    categories: ['web'],
    youtubeId: 'JleOAvKj8Ug',
    status: 'Completed',
    technologies: ['PHP', 'JavaScript', 'MySQL', 'Bootstrap', 'AJAX'],
    year: '2023'
  },
  {
    id: 'bulsufind',
    title: 'BulSUFind — Campus Lost & Found',
    image: bulsufindImg,
    alt: 'BulSUFind Mobile App Screenshot',
    brief: 'An Android app for campus item recovery featuring image-based search, real-time push notifications, and integrated peer-to-peer chat.',
    categories: ['mobile', 'web'],
    youtubeId: 'LOOvYX3b7dA',
    status: 'Capstoned',
    technologies: ['Android', 'Java', 'Firebase', 'Google Cloud Platform', 'REST APIs'],
    year: '2024'
  },
  {
    id: 'coding-chronicles',
    title: 'Coding Chronicles — 2D Platformer',
    image: codingChroniclesImg,
    alt: 'Coding Chronicles Game Screenshot',
    brief: 'A 2D action platformer game developed in Unity with custom physics, interactive level mechanics, dynamic hazards, and cross-platform Android build.',
    categories: ['game', 'mobile'],
    youtubeId: 'CZxOOZRPv8w',
    status: 'Completed',
    technologies: ['Unity', 'C#', 'Android SDK', 'Game Physics'],
    year: '2024'
  },
  {
    id: 'bulsu-classroom',
    title: 'BulSU Classroom — Assessment Engine',
    image: bulsuClassroomImg,
    alt: 'BulSU Classroom Screenshot',
    brief: 'An automated testing module featuring real-time anti-cheating browser focus monitoring, instant grading computations, and instructor analytics.',
    categories: ['web'],
    status: 'Completed',
    technologies: ['Python', 'Django', 'JavaScript', 'Browser APIs', 'Bootstrap'],
    year: '2024'
  }
];

const filters = [
  { id: 'all', name: 'All Projects' },
  { id: 'web', name: 'Web Apps' },
  { id: 'mobile', name: 'Mobile' },
  { id: 'game', name: 'Games' }
];

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredProject, setHoveredProject] = useState(null);

  const handleFilterClick = useCallback((categoryId) => {
    setActiveFilter(categoryId);
  }, []);

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.categories.includes(activeFilter));

  return (
    <section id="projects" className="projects-section">
      <div className="section-header">
        <span className="section-tag">Portfolio</span>
        <h2 className="section-title">Selected Works & Systems</h2>
        <p className="section-subtitle">
          Real-world applications, university capstone solutions, full-stack systems, and interactive software.
        </p>
      </div>

      {/* Filter Pill Tabs */}
      <div className="projects-filter-bar">
        {filters.map(filter => (
          <button
            key={filter.id}
            className={`filter-tab-btn ${activeFilter === filter.id ? 'active' : ''}`}
            onClick={() => handleFilterClick(filter.id)}
          >
            {filter.name}
          </button>
        ))}
      </div>

      {/* Modern Projects Grid */}
      <div className="projects-grid">
        {filteredProjects.map((project) => {
          const isHovered = hoveredProject === project.id;
          const showVideo = isHovered && project.youtubeId;

          return (
            <div
              key={project.id}
              className="project-bento-card"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Media Container */}
              <div className="project-media-frame">
                <div className="project-badges-strip">
                  <span className="project-status-pill">{project.status}</span>
                  <span className="project-year-pill">{project.year}</span>
                </div>

                {showVideo ? (
                  <div className="project-video-preview">
                    <iframe
                      src={`https://www.youtube.com/embed/${project.youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${project.youtubeId}&rel=0&modestbranding=1`}
                      title={`${project.title} preview`}
                      className="project-iframe"
                      allow="autoplay"
                    ></iframe>
                  </div>
                ) : (
                  <img
                    src={project.image}
                    alt={project.alt}
                    className="project-thumbnail"
                  />
                )}

                {project.youtubeId && !showVideo && (
                  <div className="video-badge-hint" title="Hover for video preview">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                    <span>Demo</span>
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="project-card-body">
                <h3 className="project-card-title">{project.title}</h3>
                <p className="project-card-brief">{project.brief}</p>

                {/* Tech Tags */}
                <div className="project-tech-pills">
                  {project.technologies.slice(0, 4).map((tech, idx) => (
                    <span key={idx} className="project-tech-pill">{tech}</span>
                  ))}
                  {project.technologies.length > 4 && (
                    <span className="project-tech-pill more">+{project.technologies.length - 4}</span>
                  )}
                </div>

                {/* Action Link */}
                <div className="project-card-action">
                  <Link to={`/project/${project.id}`} className="project-view-link">
                    <span>View Project Case</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="7" y1="17" x2="17" y2="7"></line>
                      <polyline points="7 7 17 7 17 17"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Projects;